import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Eye, Edit, CheckCircle, Trash2, Plus } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const STATUS_CONFIG = {
  DRAFT: { className: 'bg-gray-500' },
  APPROVED: { className: 'bg-green-500' },
  ARCHIVED: { className: 'bg-orange-500' },
};

const getStatusBadge = (status, t) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;
  return <Badge className={config.className}>{t(`holidayCalendarComponents.status.${status}`, { defaultValue: status })}</Badge>;
};

const formatDate = (dateString, language) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString(language, {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const CalendriersTableCard = ({
  loading,
  calendars,
  handleOpenCreateModal,
  navigateToDetails,
  handleOpenEditModal,
  handleApproveCalendar,
  confirmDelete,
  currentPage,
  setCurrentPage,
  totalCalendars,
  limit,
}) => {
  const { t, i18n } = useTranslation();
  const totalPages = Math.ceil(totalCalendars / limit);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (calendars.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-semibold text-foreground">{t("holidayCalendarComponents.table.emptyTitle")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("holidayCalendarComponents.table.emptyDesc")}
            </p>
            <div className="mt-6">
              <Button onClick={handleOpenCreateModal}>
                <Plus className="mr-2 h-4 w-4" />
                {t("holidayCalendars.newCalendar")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("holidayCommon.fields.name")}</TableHead>
                <TableHead>{t("holidayCommon.fields.year")}</TableHead>
                <TableHead>{t("holidayCommon.fields.country")}</TableHead>
                <TableHead>{t("holidayCalendarComponents.detail.status")}</TableHead>
                <TableHead className="text-center">{t("holidayCalendarComponents.table.holidays")}</TableHead>
                <TableHead className="text-center">{t("holidayCalendarComponents.table.employees")}</TableHead>
                <TableHead>{t("holidayCalendarComponents.table.createdAt")}</TableHead>
                <TableHead className="text-right">{t("holidayCommon.fields.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {calendars.map((calendar) => (
                <TableRow key={calendar.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {calendar.name}
                      {calendar.is_template && (
                        <Badge variant="outline" className="text-xs">{t("holidayCalendarComponents.table.template")}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{calendar.year}</TableCell>
                  <TableCell>{calendar.country}</TableCell>
                  <TableCell>{getStatusBadge(calendar.status, t)}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary">{calendar.holidays_count || 0}</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary">{calendar.employees_count || 0}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(calendar.created_at, i18n.language)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigateToDetails(calendar.id)}
                        title={t("holidayCalendarComponents.table.viewDetails")}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {calendar.status === 'DRAFT' && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEditModal(calendar)}
                            title={t("holidayCommon.edit")}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleApproveCalendar(calendar.id)}
                            title={t("holidayCalendarComponents.detail.approve")}
                            className="text-green-400 hover:text-green-300"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => confirmDelete(calendar)}
                        title={t("holidayCalendarComponents.deleteDialog.delete")}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-muted-foreground">
            {t("holidayCommon.page", { page: currentPage, total: totalPages })} - {t("holidayCalendarComponents.table.total", { count: totalCalendars })}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              {t("holidayCommon.previous")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              {t("holidayCommon.next")}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default CalendriersTableCard;
